<script setup>
import { computed } from "vue";
import { useConsentStatus } from "@/composables/useConsentStatus";

const { isFormDisabled } = useConsentStatus();

const props = defineProps({
  surveyData: {
    type: Object,
    required: true,
  },
  currentStage: Number,
  totalStages: Number,
  nextStageInfo: Object,
});

const emit = defineEmits(["next-stage"]);

const counties = [
  "Antrim",
  "Armagh",
  "Carlow",
  "Cavan",
  "Clare",
  "Cork",
  "Derry",
  "Donegal",
  "Down",
  "Dublin",
  "Fermanagh",
  "Galway",
  "Kerry",
  "Kildare",
  "Kilkenny",
  "Laois",
  "Leitrim",
  "Limerick",
  "Longford",
  "Louth",
  "Mayo",
  "Meath",
  "Monaghan",
  "Offaly",
  "Roscommon",
  "Sligo",
  "Tipperary",
  "Tyrone",
  "Waterford",
  "Westmeath",
  "Wexford",
  "Wicklow",
];

const workArrangements = [
  "Completely office-based (5 days traveling to an office location)",
  "Completely remote (0 days traveling to an office location)",
  "Hybrid",
];

const hybridDays = [
  "4 days per week",
  "3 days per week",
  "2 days per week",
  "1 day per week",
];

// Computed properties for conditional display
const showCommutingFrequency = computed(() => {
  return props.surveyData.workArrangement === "Hybrid";
});

const showOfficeLocation = computed(() => {
  return (
    props.surveyData.workArrangement ===
      "Completely office-based (5 days traveling to an office location)" ||
    props.surveyData.workArrangement === "Hybrid"
  );
});

const isFormValid = computed(() => {
  const residencyOk =
    !!props.surveyData.residencyCounty &&
    String(props.surveyData.residencyCounty).trim() !== "";
  const workArrangementOk =
    !!props.surveyData.workArrangement &&
    String(props.surveyData.workArrangement).trim() !== "";

  // Conditional requirements
  const commutingOk =
    !showCommutingFrequency.value ||
    (!!props.surveyData.commutingFrequency &&
      String(props.surveyData.commutingFrequency).trim() !== "");
  const officeLocationOk =
    !showOfficeLocation.value ||
    (!!props.surveyData.officeLocation &&
      String(props.surveyData.officeLocation).trim() !== "");

  return residencyOk && workArrangementOk && commutingOk && officeLocationOk;
});

const handleNext = () => {
  // "required" attributes don't block because we aren't submitting a native form.
  // Enforce required answers before progressing.
  if (!isFormValid.value) {
    const missing = [];
    if (
      !props.surveyData.residencyCounty ||
      String(props.surveyData.residencyCounty).trim() === ""
    ) {
      missing.push("Select your residency county.");
    }
    if (
      !props.surveyData.workArrangement ||
      String(props.surveyData.workArrangement).trim() === ""
    ) {
      missing.push("Select your work arrangement.");
    }
    if (
      showCommutingFrequency.value &&
      (!props.surveyData.commutingFrequency ||
        String(props.surveyData.commutingFrequency).trim() === "")
    ) {
      missing.push("Select your commuting frequency.");
    }
    if (
      showOfficeLocation.value &&
      (!props.surveyData.officeLocation ||
        String(props.surveyData.officeLocation).trim() === "")
    ) {
      missing.push("Select your office location county.");
    }

    alert(missing.join("\n"));
    return;
  }

  emit("next-stage");
};
</script>

<template>
  <div class="survey-form">
    <img
      src="@/assets/cyberireland_logo.png"
      alt="Cyber Ireland Logo"
      class="logo"
      style="width: 300px; margin: 0 auto; display: block"
    />
    <h2>Residency & Work Arrangements</h2>

    <div class="intro-text">
      <p>
        This section helps us understand your residency and work arrangement to
        calculate the Talent Magnetism Index (TMI).
      </p>
    </div>

    <!-- Question 1: Residency -->
    <div class="form-group">
      <label for="residencyCounty"
        >What county in Ireland do you primarily reside in?</label
      >
      <select
        id="residencyCounty"
        v-model="surveyData.residencyCounty"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select a county</option>
        <option v-for="county in counties" :key="county" :value="county">
          {{ county }}
        </option>
      </select>
    </div>

    <!-- Question 2: Work Arrangement -->
    <div class="form-group">
      <label for="workArrangement"
        >Which best describes your primary work arrangement?</label
      >
      <select
        id="workArrangement"
        v-model="surveyData.workArrangement"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select work arrangement</option>
        <option
          v-for="arrangement in workArrangements"
          :key="arrangement"
          :value="arrangement"
        >
          {{ arrangement }}
        </option>
      </select>
    </div>

    <!-- Question 2a: Commuting Frequency (Conditional) -->
    <div class="form-group" v-if="showCommutingFrequency">
      <label for="commutingFrequency"
        >On average, how many days a week do you travel to an office
        location?</label
      >
      <select
        id="commutingFrequency"
        v-model="surveyData.commutingFrequency"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select frequency</option>
        <option
          v-for="frequency in hybridDays"
          :key="frequency"
          :value="frequency"
        >
          {{ frequency }}
        </option>
      </select>
    </div>

    <!-- Question 3: Designated Office Location (Conditional) -->
    <div class="form-group" v-if="showOfficeLocation">
      <label for="officeLocation"
        >Which county is your designated office located in?</label
      >
      <select
        id="officeLocation"
        v-model="surveyData.officeLocation"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select a county</option>
        <option v-for="county in counties" :key="county" :value="county">
          {{ county }}
        </option>
      </select>
    </div>

    <div class="button-group">
      <button
        @click="handleNext"
        class="primary-button"
        :disabled="!isFormValid || isFormDisabled"
      >
        Next
      </button>
    </div>
  </div>
</template>

<style scoped>
.intro-text {
  background-color: var(--surface-color);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-color);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.4;
}

.form-group select {
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  cursor: pointer;
}

.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(66, 184, 131, 0.2);
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.primary-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.primary-button:hover {
  background-color: var(--primary-hover);
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
  .intro-text {
    padding: 0.875rem;
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-group label {
    font-size: 0.95rem;
  }

  .form-group select {
    padding: 0.75rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .button-group {
    margin-top: 1.5rem;
    gap: 0.75rem;
  }

  .primary-button {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
    min-height: 44px;
  }
}

@media (max-width: 480px) {
  .intro-text {
    padding: 0.75rem;
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    font-size: 0.9rem;
  }

  .form-group select {
    padding: 0.625rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  .button-group {
    margin-top: 1.25rem;
    gap: 0.625rem;
  }

  .primary-button {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    min-height: 40px;
  }
}
</style>
